import { FC, Suspense, lazy, useEffect, useState } from 'react';
import { Navigate, useMatch } from 'react-router';
import { useAppGlobStore } from 'src/appGlobalStore';
import { XmSpinner } from 'src/component/spinner/XmSpinner';
import {
  AlertingSection, ALERTING_SECTIONS, getAllowedAlertingSections, getDefaultAlertingSectionPath, resolveAlertingSection
} from 'src/content/tools/alerting/alertingMenu';
import { RoutePath } from 'src/data/Routes';
import { MenuNode } from 'src/model/MenuNode';
import { commitSelection } from 'src/routing/domain/NavDomain';
import { Log } from 'src/service/log.service';
import { GLOB } from 'src/util/Glob';

const Alerting = lazy(() => import('src/content/tools/alerting/Alerting'));

export const RouteAlerting: FC = () => {
  const match = useMatch(RoutePath.MATCH_ALERTING);
  const [isReady, setIsReady] = useState(false);
  const [menuChildren, setMenuChildren] = useState<AlertingSection[]>(null);
  // userInfo.isAdmin is only filled after xormonReady; resolving earlier would hide admin sections from admins
  useEffect(() => {
    let mounted = true;
    GLOB.xormonReady.promise.then(() => mounted && setIsReady(true),
      (reason: Error) => Log.error('Failed to resolve xormonReady for Alerting route', reason));
    return () => {
      mounted = false;
    };
  }, []);

  const section = isReady ? resolveAlertingSection(ALERTING_SECTIONS, match?.params.section, GLOB.userInfo.isAdmin) : null;

  useEffect(() => {
    if (!isReady) return;
    // copies, not the shared constants: mapParent mutates `parent`
    const children: AlertingSection[] = getAllowedAlertingSections(ALERTING_SECTIONS, GLOB.userInfo.isAdmin)
      .map(node => ({ ...node }));
    const root: MenuNode = { key: RoutePath.ALERTING, title: 'Alerting', children };
    MenuNode.mapParent(children, root);
    commitSelection(RoutePath.ALERTING, null, null, null);
    useAppGlobStore.setState({ leftMenu: root });
    setMenuChildren(children);
  }, [isReady]);

  useEffect(() => {
    if (!section || !menuChildren) return;
    GLOB.selectedItem = menuChildren.find(node => node.path === section.path);
  }, [section, menuChildren]);

  if (!isReady) return <XmSpinner />;
  if (!section) return <Navigate to={getDefaultAlertingSectionPath(GLOB.userInfo.isAdmin)} replace />;

  return <Suspense fallback={<XmSpinner />}><Alerting section={section} /></Suspense>;
};
